// ghostOverlay.ts — P1.2b GHOST-ROUTE renderer glue (Phaser side of src/render/ghostRoute.ts).
//
// Kept OUT of GameScene so the scene wiring is three tiny call-sites. Paints the friend's route as
// FAINT, spoiler-aware footprints: one small corner glyph per cell the friend actually acted on —
// ate-correct (✓ green), ate-decoy (✗ red), threaded an armed decoy (◇ amber) — matching the
// spoiler-free share legend. Un-touched cells get NOTHING (no leak). Markers live at depth 3.5 (above
// the cells, below the munchers) in each cell's bottom-left corner, clear of the centered answer +
// the status/lie glyphs, and are retired one-way as the player reaches each cell.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { ghostMarkerForId, type GhostOutcome } from './ghostRoute';
import { motionAllowed } from './settings';

export type GhostMarkerMap = Map<number, Phaser.GameObjects.Text>;

/** Spoiler-free role → board palette colour, matching the share legend (green / red / amber). */
const ROLE_COLOR = { correct: PALETTE.correct, decoy: PALETTE.hazard, threaded: PALETTE.armed } as const;

/**
 * Build the faint footprint markers for a challenge board from the decoded outcomes + the grid
 * geometry (all numbers, so GameScene can pass its private fields directly — no closure). Returns a
 * Map keyed by cell id (empty when there is no ghost). Markers are scene-owned Text at depth 3.5 (above
 * cells, below munchers), parked in each cell's bottom-left corner; under motion each gets a gentle,
 * column-phase-offset alpha breath so the trail reads as a "ghost" without pulling focus.
 */
export function buildGhostOverlay(
  scene: Phaser.Scene,
  outcomes: GhostOutcome[],
  cellCount: number,
  gridX: number,
  gridY: number,
  cellW: number,
  cellH: number,
  cols: number,
): GhostMarkerMap {
  const markers: GhostMarkerMap = new Map();
  if (outcomes.length === 0) return markers;
  const size = Math.max(11, Math.min(16, Math.floor(cellW * 0.26)));
  for (let id = 0; id < cellCount; id += 1) {
    const marker = ghostMarkerForId(outcomes, id);
    if (!marker) continue;
    const cx = gridX + (id % cols) * cellW + cellW / 2;
    const cy = gridY + Math.floor(id / cols) * cellH + cellH / 2;
    const t = scene.add
      .text(cx - cellW * 0.3, cy + cellH * 0.28, marker.glyph, {
        fontFamily: FONT.family,
        fontSize: `${size}px`,
        color: hex(ROLE_COLOR[marker.role]),
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0.4)
      .setDepth(3.5);
    markers.set(id, t);
    if (motionAllowed()) {
      scene.tweens.add({
        targets: t,
        alpha: { from: 0.26, to: 0.5 },
        duration: 1100,
        delay: (id % cols) * 80,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }
  return markers;
}

/** The cell fields pruneGhostMarkers reads (a structural subset of the Core Cell). */
export interface GhostLiveCell {
  id: number;
  eaten: boolean;
  armed: boolean;
  revealedUntilMs: number;
}

/**
 * Retire a footprint once the player reaches/acts on its cell (eaten, armed, or scan-revealed) — the
 * live state cue now owns the tile and the ghost would just be noise. One-way (cells never return to
 * neutral); the marker fades then self-destructs and drops from the map, so the per-frame call stops
 * cheaply once every footprint has been overtaken. Pass snap.cells + the sim time each frame.
 */
export function pruneGhostMarkers(
  scene: Phaser.Scene,
  markers: GhostMarkerMap,
  cells: GhostLiveCell[],
  timeMs: number,
): void {
  for (const cell of cells) {
    const marker = markers.get(cell.id);
    if (!marker) continue;
    const live = cell.eaten || cell.armed || cell.revealedUntilMs > timeMs;
    if (!live) continue;
    markers.delete(cell.id);
    scene.tweens.killTweensOf(marker);
    if (motionAllowed() && marker.active) {
      scene.tweens.add({
        targets: marker,
        alpha: 0,
        scale: 0.5,
        duration: 220,
        ease: 'Quad.easeIn',
        onComplete: () => marker.destroy(),
      });
    } else {
      marker.destroy();
    }
  }
}

/** Hard teardown on scene SHUTDOWN: kill any breath/fade tweens and destroy every marker. */
export function destroyGhostMarkers(scene: Phaser.Scene, markers: GhostMarkerMap): void {
  for (const m of markers.values()) {
    scene.tweens.killTweensOf(m);
    m.destroy();
  }
  markers.clear();
}
